import { Stack } from '@daily/shared/components/Stack';
import { useResize } from '@daily/shared/hooks/useResize';
import { useResizeObserver } from '@daily/shared/hooks/useResizeObserver';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useBreakoutSessionState } from '/components/BreakoutRooms/hooks/useBreakoutSessionState';
import { RecordingState } from '/components/Call/RecordingState';
import { PictureInPictureButton } from '/features/picture-in-picture/components/PictureInPictureButton';
import { useCallConfig } from '/hooks/useCallConfig';
import { isSafari } from '/lib/browserConfig';

import { BreakoutStatus } from './BreakoutStatus';
import { LiveBadge } from './LiveBadge';
import { PeopleInCall } from './PeopleInCall';
import { SettingsButton } from './SettingsButton';
import { TopbarAction } from './TopbarAction';

export const Topbar: React.FC = memo(() => {
  const { enablePIPUI, poweredByDaily } = useCallConfig();
  const isSafari14OrLower = isSafari(1, 14);
  const { breakoutStatus } = useBreakoutSessionState();

  const topbarRef = useRef<HTMLDivElement>(null);
  const [supportsPIP, setSupportsPIP] = useState(false);

  /**
   * Recalculates and sets the --header-height CSS variable.
   */
  const updateCSSHeaderHeight = useCallback(() => {
    if (!topbarRef.current) return;
    const { height, top } = topbarRef.current.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height + top}px`);
  }, []);
  /**
   * Set up ResizeObserver to determine when Topbar shifts position.
   */
  useResizeObserver(topbarRef, updateCSSHeaderHeight);
  useResize(updateCSSHeaderHeight);
  useEffect(() => {
    updateCSSHeaderHeight;
  }, [poweredByDaily, updateCSSHeaderHeight]);

  useEffect(() => {
    // TODO: Figure out why pictureInPicture types are missing in `next build`
    // @ts-ignore
    setSupportsPIP(document.pictureInPictureEnabled);
  }, []);

  return (
    <div ref={topbarRef} className="topbar">
      <Stack align="center" justify="space-between" horizontal>
        <Stack align="center" horizontal>
          <SettingsButton />
          <Stack align="center" gap={4} horizontal>
            <LiveBadge />
            {breakoutStatus === 'ongoing' ? (
              <BreakoutStatus />
            ) : (
              <PeopleInCall />
            )}
          </Stack>
        </Stack>
        <RecordingState />
        <div className="action">
          <TopbarAction />
          {/* document.pictureInPictureEnabled is available from Safari 13, but PiP throws an error in Safari 14.
           So disable PiP on anything that's not Safari >15. */}
          {enablePIPUI && supportsPIP && !isSafari14OrLower && (
            <PictureInPictureButton />
          )}
        </div>
      </Stack>
      <style jsx>{`
        .topbar {
          background: var(--card-bg);
          border-bottom: 1px solid var(--card-border);
          flex: none;
          padding: 4px 8px;
          width: 100vw;
          z-index: var(--zindex-tray);
        }
        .action {
          display: flex;
        }
      `}</style>
    </div>
  );
});
Topbar.displayName = 'Topbar';
